import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, getClientMetadata } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { Mail, AlertCircle, CheckCircle, Sun, Moon } from 'lucide-react';
import { useTranslations } from '../../i18n';

import { supabase } from '../../lib/supabase';
const favicon = '/favicon_clear.png';
const faviconDark = '/favicon_clear_dark.png';
import { LanguageSelector } from '../../components/ui/LanguageSelector';

export function LoginForm() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const { signInWithMagicLink, user, loading: authLoading } = useAuth();
  const { preferences, setTheme } = usePreferences();
  const { t, language } = useTranslations();

  if (!authLoading && user) {
    return <Navigate to="/" replace />;
  }

  const handleGoogleLogin = async () => {
    setIsGoogleSubmitting(true);
    // Log the initiation of Google login
    // Logging google_initiated to DB...
    void (async () => { 
      try { 
        const metadata = await getClientMetadata();
        await supabase.rpc('log_login_attempt', { 
          p_email: 'unknown', 
          p_provider: 'google_initiated', 
          p_metadata: metadata 
        }); 
      } catch {} 
    })();

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });
    if (error) {
      setError(error.message);
      setIsGoogleSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsSubmitting(true);

    try {
      // Log explicit magic link click
      // Logging magic_link_initiated to DB...
      void (async () => {
        try {
          const metadata = await getClientMetadata();
          await supabase.rpc('log_login_attempt', {
            p_email: email,
            p_provider: 'magic_link_initiated',
            p_metadata: metadata
          });
        } catch {}
      })();

      const { error } = await signInWithMagicLink(email, language || 'en');
      if (error) {
        setError(t.auth.magicLinkError);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError(t.auth.genericError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center px-4 py-8 font-primary overflow-hidden">
      <div className="bg-page-gradient" />
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white/80 dark:bg-slate-900/60 rounded-3xl shadow-2xl p-6 sm:p-8 border border-gray-100 dark:border-white/5 backdrop-blur-xl animate-in fade-in zoom-in-95 duration-500">
          {/* Theme/Language Controls - Now inside card */}
          <div className="flex items-center justify-end gap-2 mb-4">
            <LanguageSelector variant="ghost" useModal={true} />
            <button
              onClick={() => setTheme(preferences?.theme === 'dark' ? 'light' : 'dark')}
              className="p-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-all text-gray-600 dark:text-gray-300"
              aria-label="Toggle Theme"
            >
              {preferences?.theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>

          <div className="text-center mb-6">
            <div className="flex flex-row items-center justify-center gap-3 mb-4">
              <img src={preferences?.theme === 'dark' ? faviconDark : favicon} alt="Logo" className="w-10 h-10 sm:w-12 sm:h-12 object-contain" />
              <h1 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white tracking-tight">
                My Car Costs
              </h1>
            </div>

            <div className="mx-auto w-10 h-1 bg-gray-100 dark:bg-white/10 rounded-full mb-4"></div>

            <h2 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white mb-2">
              {t.auth.signIn}
            </h2>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl flex items-center gap-3 animate-in shake duration-500">
              <AlertCircle size={18} className="text-red-600 dark:text-red-400 flex-shrink-0" />
              <p className="text-red-800 dark:text-red-300 text-sm font-medium">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-xl flex items-center gap-3 animate-in zoom-in-95">
              <CheckCircle size={18} className="text-green-600 dark:text-green-400 flex-shrink-0" />
              <div>
                <p className="text-green-800 dark:text-green-300 text-sm font-bold mb-1">{t.auth.magicLinkSent}</p>
                <p className="text-green-700 dark:text-green-400 text-xs font-medium">{t.auth.magicLinkCheckEmail}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 ml-1">
                {t.auth.emailAddress}
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-600">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting || success}
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all disabled:opacity-50 dark:text-white text-sm sm:text-base font-bold placeholder:text-gray-300 dark:placeholder:text-gray-700"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || success || isGoogleSubmitting}
              className="w-full px-6 py-2.5 sm:py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-2xl hover:bg-blue-700 dark:hover:bg-blue-600 transition-all font-black shadow-xl shadow-blue-500/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base tracking-tight"
            >
              {isSubmitting ? t.auth.sending : success ? t.auth.linkSent : t.auth.getMagicLink}
            </button>
          </form>

          <div className="relative my-6 sm:my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-100 dark:border-white/5"></div>
            </div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-widest font-black text-gray-400 dark:text-gray-600">
              <span className="px-4 bg-white dark:bg-[#1E293B] dark:bg-transparent">{t.auth.orContinueWith}</span>
            </div>
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={isSubmitting || success || isGoogleSubmitting}
            className="w-full px-6 py-2.5 sm:py-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-white rounded-2xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-all font-bold flex items-center justify-center gap-3 shadow-sm active:scale-[0.98] disabled:opacity-50"
          >
            {isGoogleSubmitting ? (
              <div className="w-5 h-5 border-b-2 border-blue-600 dark:border-blue-400 rounded-full animate-spin" />
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1 .67-2.28 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.67-.35-1.39-.35-2.09s.13-1.42.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                <span className="text-xs sm:text-sm">{t.auth.googleAccount}</span>
              </>
            )}
          </button>

          <div className="mt-6 sm:mt-8 p-3 bg-blue-50/50 dark:bg-blue-500/5 border border-blue-100 dark:border-blue-500/10 rounded-xl">
            <p className="text-[10px] sm:text-xs text-blue-800 dark:text-blue-400 font-medium text-center leading-relaxed">
              {t.auth.newUserNote}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
